import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { unsubscribeService, unsubscribeApiUtils } from '../config/unsubscribeApi';

const ManageUnsubscribesPage = () => {
  const [unsubscribes, setUnsubscribes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUnsubscribes, setSelectedUnsubscribes] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchEmail, setSearchEmail] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');

  // Access password
  const ACCESS_PASSWORD = 'PASSword123$';

  // Selection helpers
  const getTopNLatestIds = (n) => {
    if (!unsubscribes?.length) return [];
    const sorted = [...unsubscribes].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
    return sorted.slice(0, n).map((u) => u.id);
  };

  const TOP_N = 100;
  const topNIds = useMemo(() => getTopNLatestIds(TOP_N), [unsubscribes]);
  const allIds = useMemo(() => unsubscribes.map((u) => u.id), [unsubscribes]);

  const allSelected = useMemo(
    () => unsubscribes.length > 0 && selectedUnsubscribes.length === unsubscribes.length,
    [unsubscribes.length, selectedUnsubscribes.length]
  );

  const noneSelected = useMemo(
    () => selectedUnsubscribes.length === 0,
    [selectedUnsubscribes.length]
  );

  const topNSelected = useMemo(() => {
    if (topNIds.length === 0) return false;
    const selectedSet = new Set(selectedUnsubscribes);
    return topNIds.every((id) => selectedSet.has(id));
  }, [topNIds, selectedUnsubscribes]);

  const selectAllRef = useRef(null);
  useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate =
      !noneSelected && !allSelected && unsubscribes.length > 0;
  }, [noneSelected, allSelected, unsubscribes.length]);

  // Auth handlers
  const handleAuth = (e) => {
    e.preventDefault();
    if (password === ACCESS_PASSWORD) {
      setIsAuthenticated(true);
      setAuthError('');
      sessionStorage.setItem('nota_unsubscribe_auth', 'true');
    } else {
      setAuthError('Invalid password. Please try again.');
      setPassword('');
    }
  };

  useEffect(() => {
    const isAuth = sessionStorage.getItem('nota_unsubscribe_auth');
    if (isAuth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('nota_unsubscribe_auth');
    setPassword('');
  };

  // Data fetch
  const fetchUnsubscribes = async (page = 1, email = '', start = '', end = '') => {
    setLoading(true);
    setError(null);

    try {
      const response = await unsubscribeService.getUnsubscribes({
        page,
        limit: 500,
        ...(email && { email }),
        ...(start && { startDate: start }),
        ...(end && { endDate: end })
      });

      setUnsubscribes(response.unsubscribes);
      setCurrentPage(response.pagination.currentPage);
      setTotalPages(response.pagination.totalPages);
      setTotalCount(response.pagination.totalCount);
      setSelectedUnsubscribes([]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchUnsubscribes();
    }
  }, [isAuthenticated]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchUnsubscribes(1, searchEmail, startDate, endDate);
  };

  const handlePageChange = (page) => {
    fetchUnsubscribes(page, searchEmail, startDate, endDate);
  };

  // Selection handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedUnsubscribes(allIds);
    } else {
      setSelectedUnsubscribes([]);
    }
  };

  const handleSelectTopN = () => {
    if (topNSelected) {
      const topSet = new Set(topNIds);
      setSelectedUnsubscribes((prev) => prev.filter((id) => !topSet.has(id)));
    } else {
      setSelectedUnsubscribes(topNIds);
    }
  };

  const handleClearSelection = () => setSelectedUnsubscribes([]);

  const handleSelectUnsubscribe = (id, checked) => {
    if (checked) {
      setSelectedUnsubscribes((prev) => [...prev, id]);
    } else {
      setSelectedUnsubscribes((prev) => prev.filter((unsubId) => unsubId !== id));
    }
  };

  // Delete handlers
  const handleDelete = async (unsubscribeIds) => {
    if (
      !window.confirm(
        `Are you sure you want to delete ${unsubscribeIds.length} unsubscribe(s)? This action cannot be undone.`
      )
    ) {
      return;
    }

    setIsDeleting(true);
    try {
      await unsubscribeService.deleteUnsubscribes(unsubscribeIds);
      fetchUnsubscribes(currentPage, searchEmail, startDate, endDate);
      setSelectedUnsubscribes([]);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  // CSV export
  const handleExportCSV = () => {
    if (unsubscribes.length === 0) return;

    const exportData = unsubscribes.map((unsub) => ({
      'Email': unsub.email,
      'Created At': formatDate(unsub.createdAt)
    }));

    const filename = `unsubscribes-${new Date().toISOString().split('T')[0]}.csv`;
    unsubscribeApiUtils.exportToCSV(exportData, filename);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Auth gate
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6">
        <motion.div
          className="max-w-md w-full bg-white rounded-lg shadow-md p-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="text-center mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Access Required</h2>
            <p className="text-gray-600">
              Please enter the password to access the unsubscribe management system.
            </p>
          </div>

          <form onSubmit={handleAuth}>
            <div className="mb-6">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#202356] focus:border-transparent"
                placeholder="Enter access password"
                required
              />
            </div>

            {authError && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md text-sm">
                {authError}
              </div>
            )}

            <button
              type="submit"
              className="w-full bg-[#202356] text-white py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors focus:ring-2 focus:ring-[#202356] focus:ring-offset-2"
            >
              Access Management System
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  // Main UI
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="py-12 px-6 md:px-12 bg-[#202356]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-3xl md:text-4xl font-semibold text-white mb-4">
              Manage Unsubscribes
            </h1>
            <p className="text-lg text-white opacity-90">
              View, search, and manage all unsubscribe requests
            </p>
            <button onClick={handleLogout} className="mt-4 text-white hover:text-gray-200 transition-colors">
              Logout
            </button>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Filters and Actions */}
          <motion.div
            className="bg-white rounded-lg shadow-md p-6 mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search by Email</label>
                <input
                  type="email"
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#202356] focus:border-transparent"
                  placeholder="Enter email address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#202356] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#202356] focus:border-transparent"
                />
              </div>

              <div className="flex items-end">
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className="w-full bg-[#202356] text-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors disabled:bg-gray-400"
                >
                  {loading ? 'Searching...' : 'Search'}
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleExportCSV}
                disabled={unsubscribes.length === 0}
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors disabled:bg-gray-400"
              >
                Export CSV
              </button>

              {selectedUnsubscribes.length > 0 && (
                <button
                  onClick={() => handleDelete(selectedUnsubscribes)}
                  disabled={isDeleting}
                  className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors disabled:bg-gray-400"
                >
                  {isDeleting ? 'Deleting...' : `Delete Selected (${selectedUnsubscribes.length})`}
                </button>
              )}
            </div>
          </motion.div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
              {error}
            </div>
          )}

          {/* Unsubscribes Table */}
          <motion.div
            className="bg-white rounded-lg shadow-md overflow-hidden"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h3 className="text-lg font-medium text-gray-900">
                  Unsubscribes ({totalCount} total)
                </h3>

                {unsubscribes.length > 0 && (
                  <div className="flex items-center gap-3">
                    <label className="flex items-center">
                      <input
                        ref={selectAllRef}
                        type="checkbox"
                        checked={allSelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="mr-2 h-4 w-4 text-[#202356] focus:ring-[#202356] border-gray-300 rounded"
                      />
                      Select All
                    </label>

                    <button
                      type="button"
                      onClick={handleSelectTopN}
                      className={`px-3 py-1 rounded-md text-sm border transition-colors ${
                        topNSelected
                          ? 'bg-[#202356] text-white border-[#202356]'
                          : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-100'
                      }`}
                      title={`Select the latest ${Math.min(TOP_N, unsubscribes.length)} by Created At`}
                    >
                      {topNSelected
                        ? `Unselect Top ${Math.min(TOP_N, unsubscribes.length)}`
                        : `Select Top ${Math.min(TOP_N, unsubscribes.length)}`}
                    </button>

                    {!noneSelected && (
                      <button
                        type="button"
                        onClick={handleClearSelection}
                        className="px-3 py-1 rounded-md text-sm border border-gray-300 hover:bg-gray-100"
                        title="Clear current selection"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                )}
              </div>
              {unsubscribes.length > 0 && (
                <p className="mt-2 text-xs text-gray-500">
                  "Top {Math.min(TOP_N, unsubscribes.length)}" = most recent by <code>createdAt</code> on this page.
                </p>
              )}
            </div>

            {loading ? (
              <div className="px-6 py-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202356] mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading unsubscribes...</p>
              </div>
            ) : unsubscribes.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-500">No unsubscribes found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Select
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created At
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {unsubscribes.map((unsub) => (
                      <tr key={unsub.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedUnsubscribes.includes(unsub.id)}
                            onChange={(e) => handleSelectUnsubscribe(unsub.id, e.target.checked)}
                            className="h-4 w-4 text-[#202356] focus:ring-[#202356] border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{unsub.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{formatDate(unsub.createdAt)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleDelete([unsub.id])}
                            disabled={isDeleting}
                            className="text-red-600 hover:text-red-900 disabled:text-gray-400"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing page {currentPage} of {totalPages}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1 || loading}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm hover:bg-gray-100 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      Previous
                    </button>

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const pageNum = Math.max(1, currentPage - 2) + i;
                      if (pageNum > totalPages) return null;

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          disabled={loading}
                          className={`px-3 py-1 border rounded-md text-sm ${
                            pageNum === currentPage
                              ? 'bg-[#202356] text-white border-[#202356]'
                              : 'border-gray-300 hover:bg-gray-100 disabled:bg-gray-100'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages || loading}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm hover:bg-gray-100 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ManageUnsubscribesPage;
